import { create } from "zustand";

import type { CalendarRequest } from "src/core/backend/clients/backendClient";
import { logService } from "src/core/services/logService";
import { CalendarEvent } from "../models/calendarEvent";
import { calendarService } from "./calendarService";

interface CalendarState {
  readonly loading: boolean;
  readonly events: CalendarEvent[];
  readonly nextAvailableSlotDate?: Date;

  clear: () => void;

  calendarSlots: (
    request: CalendarRequest,
    officeHours: boolean,
    firstAvailableNeed?: boolean
  ) => void;
}

export const useCalendarStore = create<CalendarState>()((set) => ({
  loading: false,

  events: [],

  clear: () => {
    set({
      loading: false,
      events: [],
    });
  },

  calendarSlots: async (
    request: CalendarRequest,
    officeHours: boolean = false,
    firstAvailableNeed?: boolean
  ) => {
    set({ loading: true });

    if (logService.globalLog)
      logService.info(
        "calendarSlots request",
        request,
        officeHours,
        firstAvailableNeed
      );

    const response = await calendarService
      .calendarSlots(request)
      .then((response) => {
        if (logService.globalLog)
          logService.info("calendarSlots response", response);
        return response;
      })
      .catch((error) => {
        logService.error("calendarSlots", error);
        set({ loading: false });
        throw error;
      });

    const nextAvailableSlotDate =
      response?.slots?.length === 0 || !!firstAvailableNeed
        ? response?.nextAvailableSlotDate
        : undefined;

    set({
      nextAvailableSlotDate,
    });

    if (!!nextAvailableSlotDate) {
      return;
    }

    const events = [
      ...(officeHours
        ? (response?.officeHours ?? []).map((officeHour) => {
            return new CalendarEvent({
              from: officeHour.from,
              to: officeHour.to,
              type: officeHour.status ?? "",
            });
          })
        : []),
      ...(response?.slots ?? []).map((slot) => {
        return new CalendarEvent({
          from: slot.from,
          to: slot.to,
          type: "Slot",
        });
      }),
    ];

    if (logService.globalLog) logService.info("calendarSlots events", events);

    set({ events, loading: false });
  },
}));
